class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.totalCounts = {};

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
    }

    this.predictiveWrapper = this.querySelector("[data-predictive-search]");
    if (this.predictiveWrapper) {
      this.predictiveSearchResults = this.predictiveWrapper.querySelector(
        ".predictive-search__results.results"
      );
    }

    this.onBodyClick = this.handleBodyClick.bind(this);
    
    this.isOpen = false;
    this.allTriggers = document.querySelectorAll('.js-search-toggle');
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.allTriggers.forEach((button) => {
      button.addEventListener('click', () => {
        if (this.getAttribute("open")) {
          this.close();
        } else
          this.open();
      });
    });

    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.predictiveWrapper.setAttribute("data-show-results", false);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) return;

    this.getSearchResults(searchTerm);
  }

  onKeyup(event) {
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll("li");
    let activeElement = this.querySelector("li");

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement =
        selectedElement.previousElementSibling ||
        allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      document.dispatchEvent(new Event('mobile-gallery:refresh'));
      
      if (this.totalCounts[queryKey]) {
        this.updateResultsCount(this.totalCounts[queryKey]);
      }
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,query&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);

        document.dispatchEvent(new Event('mobile-gallery:refresh'));
        return fetch(`/search?q=${encodeURIComponent(searchTerm)}`);
      })
      .then(res => res.text())
      .then(text => {
        const doc = new DOMParser().parseFromString(text, "text/html");
        const searchPageCount = doc.querySelector(".search-results__count");
        const totalCount = searchPageCount?.innerText || '';

        this.totalCounts[queryKey] = totalCount;

        this.updateResultsCount(totalCount);
      })
      .catch((error) => {
        console.error(error);
        if (this?.close) this.close();
      });
  }

  updateResultsCount(totalCount) {
    const viewAllBtns = document.querySelectorAll(".js-results-count");
    viewAllBtns.forEach(el => {
      const parent = el.closest('.search-results__count-block');
      if (parent) {
        el.innerText = totalCount;
        parent.classList.remove('hidden');
      } else {
        el.innerText = `(${totalCount})`;
      }
    });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.predictiveWrapper.setAttribute("data-show-results", true);
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    this.input.focus();

    document.querySelector('.mega-menu').classList.remove('active');
    document.body.classList.add('blur');

    document.body.addEventListener("click", this.onBodyClick, {
      passive: true,
    });
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      if (this.input) {
        this.input.value = "";
      }

      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    if (this.input) {
      this.input.setAttribute("aria-activedescendant", "");
      this.input.setAttribute("aria-expanded", false);
    }

    this.removeAttribute("open");
    document.body.classList.remove('blur');

    document.body.removeEventListener("click", this.onBodyClick, {
      passive: true,
    });

    this.isOpen = false;
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a')
  }

  onFormReset() {
    if (this.shouldResetForm()) {
      this.searchTerm = '';
      this.input.focus();
      this.predictiveWrapper.setAttribute("data-show-results", false);
    }
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (!target.closest("predictive-search") && !target.closest('.js-search-toggle')) {
      this.close(true);
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);